import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, MapPin, ArrowRight, Plus, User as UserIcon, ClipboardList, CheckCircle2, Circle, Trash2 } from "lucide-react";
import { statusMeta, formatDate, roleLabel } from "../lib/format";
import TaskFormDialog from "../components/TaskFormDialog";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseDate = (s) => {
  if (!s) return null;
  // Accept YYYY-MM-DD or ISO
  const str = s.length > 10 ? s.slice(0, 10) : s;
  const [y, m, d] = str.split("-").map(Number);
  if (!y) return null;
  return new Date(y, m - 1, d);
};
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function buildMonthGrid(year, month) {
  // Returns 6 weeks x 7 days = 42 dates starting from the Sunday on/before the 1st.
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay()); // back to Sunday
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function projectSpans(project, day) {
  const s = parseDate(project.start_date);
  const e = parseDate(project.end_date) || s;
  if (!s) return false;
  const t = day.getTime();
  return t >= new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime() &&
         t <= new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime();
}

const statusChipCls = (status) => {
  if (status === "active") return "bg-blue-800 text-white";
  if (status === "upcoming") return "bg-amber-400 text-amber-950";
  if (status === "completed") return "bg-emerald-600 text-white";
  return "bg-slate-200 text-slate-800";
};

export default function CalendarPage() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(today);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const navigate = useNavigate();

  const loadProjects = () => api.get("/projects", { params: { archived: false } }).then((r) => setProjects(r.data));
  const loadTasks = () => {
    // pull a wide window covering the current 6-week grid
    const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
    const start = iso(grid[0]);
    const end = iso(grid[grid.length - 1]);
    return api.get("/tasks", { params: { start, end } }).then((r) => setTasks(r.data));
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const grid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const projectsOnDay = (day) => projects.filter((p) => projectSpans(p, day));
  const tasksOnDay = (day) => tasks.filter((t) => t.date === iso(day));

  const monthName = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const jump = (delta) => {
    const c = new Date(cursor);
    c.setMonth(c.getMonth() + delta);
    setCursor(c);
  };
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelected(t);
  };

  const selectedProjects = selected ? projectsOnDay(selected) : [];
  const selectedTasks = selected ? tasksOnDay(selected) : [];

  // group tasks by assignee for the selected day
  const tasksByAssignee = useMemo(() => {
    const g = new Map();
    for (const t of selectedTasks) {
      const key = t.assignee_id || "__unassigned__";
      const bucket = g.get(key) || { assignee_id: t.assignee_id, name: t.assignee_name || "Unassigned", role: t.assignee_role, tasks: [] };
      bucket.tasks.push(t);
      g.set(key, bucket);
    }
    return Array.from(g.values());
  }, [selectedTasks]);

  const toggleTask = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "pending" : "done" });
      await loadTasks();
    } catch (e) { toast.error("Update failed"); }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      await loadTasks();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Schedule"
        title="Project Calendar"
        description="A monthly view of every active and upcoming project. Click a day to see what's scheduled."
        testId="calendar-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={goToday} className="rounded-sm border-slate-300" data-testid="calendar-today-btn">
              Today
            </Button>
            <div className="flex items-center border border-slate-300 rounded-sm">
              <Button variant="ghost" size="icon" onClick={() => jump(-1)} aria-label="Previous month" data-testid="calendar-prev-btn" className="rounded-none">
                <ChevronLeft size={16} />
              </Button>
              <div className="px-3 text-sm font-semibold font-mono min-w-[180px] text-center" data-testid="calendar-month-label">
                {monthName}
              </div>
              <Button variant="ghost" size="icon" onClick={() => jump(1)} aria-label="Next month" data-testid="calendar-next-btn" className="rounded-none">
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        }
      />

      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar grid */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {WEEKDAYS.map((w) => (
              <div key={w} className="p-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 grid-rows-6" data-testid="calendar-grid">
            {grid.map((day, idx) => {
              const inMonth = day.getMonth() === cursor.getMonth();
              const isToday = sameDay(day, today);
              const isSelected = sameDay(day, selected);
              const dayProjects = projectsOnDay(day);
              const dayTasks = tasksOnDay(day);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelected(day)}
                  data-testid={`day-cell-${iso(day)}`}
                  className={`text-left min-h-[70px] md:min-h-[110px] p-1.5 md:p-2 border-b border-r border-slate-200 transition-colors relative
                    ${idx % 7 === 6 ? "border-r-0" : ""}
                    ${!inMonth ? "bg-slate-50/60" : "bg-white hover:bg-slate-50"}
                    ${isSelected ? "ring-2 ring-inset ring-blue-800" : ""}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center gap-1 text-xs font-mono ${!inMonth ? "text-slate-400" : "text-slate-700"} ${isToday ? "font-bold" : ""}`}>
                      {isToday && <span className="grid place-items-center h-5 w-5 rounded-sm bg-blue-800 text-white text-[10px] font-bold">{day.getDate()}</span>}
                      {!isToday && <span>{day.getDate()}</span>}
                    </div>
                    {dayTasks.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-blue-800">
                        <ClipboardList size={10} /> {dayTasks.length}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayProjects.slice(0, 3).map((p) => (
                      <div
                        key={p.id}
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm truncate ${statusChipCls(p.status)}`}
                        title={p.name}
                      >
                        {p.name}
                      </div>
                    ))}
                    {dayProjects.length > 3 && (
                      <div className="text-[10px] text-slate-500 font-mono">+{dayProjects.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Day details rail */}
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-800">
              <CalendarDays size={14} />
              {selected
                ? selected.toLocaleDateString(undefined, { weekday: "long" })
                : "Select a day"}
            </div>
            <div className="mt-1 font-display text-2xl font-black tracking-tighter text-slate-900">
              {selected ? formatDate(selected) : "—"}
            </div>
            <div className="mt-1 text-xs text-slate-500 font-mono">
              {selectedProjects.length} project{selectedProjects.length === 1 ? "" : "s"} · {selectedTasks.length} task{selectedTasks.length === 1 ? "" : "s"}
            </div>
            <Button
              onClick={() => { setEditingTask(null); setTaskDialogOpen(true); }}
              className="mt-3 w-full rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
              data-testid="calendar-add-task-btn"
              disabled={!selected}
            >
              <Plus size={14} className="mr-1.5" /> Add Task for This Day
            </Button>
          </div>

          {/* Tasks grouped by assignee */}
          {tasksByAssignee.length > 0 && (
            <div className="space-y-3" data-testid="calendar-day-tasks">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Assignments</div>
              {tasksByAssignee.map((g) => (
                <div key={g.assignee_id || "unassigned"} className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <div className={`grid place-items-center h-6 w-6 rounded-sm ${g.assignee_id ? "bg-blue-800 text-white" : "bg-slate-300 text-slate-700"} text-[10px] font-bold`}>
                      {g.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{g.name}</div>
                      {g.role && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{roleLabel(g.role)}</div>}
                    </div>
                    <span className="text-[10px] font-mono text-slate-500">{g.tasks.length}</span>
                  </div>
                  <ul className="divide-y divide-slate-200">
                    {g.tasks.map((t) => {
                      const done = t.status === "done";
                      return (
                        <li key={t.id} className="p-3 flex items-start gap-2" data-testid={`task-item-${t.id}`}>
                          <button
                            type="button"
                            onClick={() => toggleTask(t)}
                            className="mt-0.5 shrink-0"
                            aria-label={done ? "Mark pending" : "Mark done"}
                          >
                            {done
                              ? <CheckCircle2 size={16} className="text-emerald-600" />
                              : <Circle size={16} className="text-slate-400" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-semibold ${done ? "line-through text-slate-500" : "text-slate-900"}`}>
                              {t.title}
                              {t.quantity != null && (
                                <span className="ml-1 font-mono text-xs text-slate-500">× {t.quantity}</span>
                              )}
                            </div>
                            {t.location && (
                              <div className="mt-0.5 text-xs text-slate-600 inline-flex items-center gap-1">
                                <MapPin size={11} /> {t.location}
                              </div>
                            )}
                            {t.project_name && (
                              <button
                                type="button"
                                onClick={() => navigate(`/projects/${t.project_id}`)}
                                className="mt-0.5 text-[11px] uppercase tracking-widest font-bold text-blue-800 hover:underline"
                              >
                                {t.project_name}
                              </button>
                            )}
                            {t.description && (
                              <div className="mt-1 text-xs text-slate-600">{t.description}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => { setEditingTask(t); setTaskDialogOpen(true); }}
                            className="text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-blue-800"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteTask(t)}
                            className="text-slate-400 hover:text-red-600"
                            aria-label="Delete task"
                            data-testid={`task-delete-${t.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Projects on this day */}
          <div className="space-y-2" data-testid="calendar-day-projects">
            {selectedProjects.length === 0 ? (
              <div className="border border-dashed border-slate-300 bg-white rounded-md p-6 text-center text-sm text-slate-500">
                Nothing scheduled on this day.
              </div>
            ) : (
              selectedProjects.map((p) => {
                const meta = statusMeta(p.status, p.archived);
                return (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    data-testid={`calendar-project-${p.id}`}
                    className="w-full text-left bg-white border border-slate-200 hover:border-blue-800 rounded-md p-4 shadow-sm transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                        {p.client && <div className="text-xs text-slate-600 mt-0.5 truncate">{p.client}</div>}
                        {p.location && (
                          <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                            <MapPin size={12} /> {p.location}
                          </div>
                        )}
                      </div>
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="mt-2 text-xs font-mono text-slate-500">
                      {formatDate(p.start_date)} → {formatDate(p.end_date || p.start_date)}
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-800 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open project <ArrowRight size={12} />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Legend */}
          <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Legend</div>
            <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
              <span className="px-2 py-0.5 rounded-sm bg-amber-400 text-amber-950">Upcoming</span>
              <span className="px-2 py-0.5 rounded-sm bg-blue-800 text-white">Active</span>
              <span className="px-2 py-0.5 rounded-sm bg-emerald-600 text-white">Completed</span>
            </div>
          </div>
        </aside>
      </div>

      <TaskFormDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={editingTask}
        defaultDate={selected ? iso(selected) : ""}
        onSuccess={loadTasks}
      />
    </div>
  );
}
